import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { ArrowRight, Infinity as InfinityIcon } from "lucide-react";

export default function RegisterPage() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [invite, setInvite] = useState("");
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr(null);
    setBusy(true);
    try {
      await register(email.trim().toLowerCase(), password, name.trim(), invite.trim());
      nav("/dashboard", { replace: true });
    } catch (e) {
      setErr(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F3F8F7] text-[#212121] px-6">
      <div className="w-full max-w-md k-card p-10">
        <div className="flex items-center gap-2">
          <InfinityIcon className="h-7 w-7 text-[#156F5B]" strokeWidth={2.5} />
          <span className="text-2xl font-bold font-heading text-[#156F5B]">KOIOC</span>
        </div>
        <h2 className="mt-8 text-3xl font-bold font-display text-[#212121]">Create your account</h2>
        <p className="mt-2 text-[#4D4D4D] text-sm">
          This portal is invitation only. Paste the code from your invitation email, or use the link it
          contains to set your password directly.
        </p>

        <div className="mt-5">
          <label className="k-sublabel">Invitation code</label>
          <input
            value={invite}
            onChange={(e) => setInvite(e.target.value)}
            required
            data-testid="register-invite-input"
            className="k-input mt-1"
            placeholder="Paste your invitation code"
          />
        </div>

        <form onSubmit={submit} className="mt-8 space-y-5" data-testid="register-form">
          <div>
            <label className="k-sublabel">Name</label>
            <input
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              data-testid="register-name-input"
              className="k-input mt-2"
              placeholder="Jane Doe"
            />
          </div>
          <div>
            <label className="k-sublabel">Email</label>
            <input
              type="email"
              required
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="register-email-input"
              className="k-input mt-2"
              placeholder="you@company.com"
            />
          </div>
          <div>
            <label className="k-sublabel">Password</label>
            <input
              type="password"
              required
              minLength={6}
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="register-password-input"
              className="k-input mt-2"
              placeholder="Min 6 characters"
            />
          </div>
          {err && (
            <div className="text-sm text-red-700 rounded-md border border-red-200 bg-red-50 px-4 py-2" data-testid="register-error">
              {err}
            </div>
          )}
          <button type="submit" disabled={busy} data-testid="register-submit-button" className="k-btn group">
            {busy ? "Creating…" : "Create account"}
            <ArrowRight className="h-4 w-4 group-hover:translate-x-0.5" style={{ transition: "transform .2s ease" }} />
          </button>
          <div className="text-xs text-[#6B7280]">
            Already have an account?{" "}
            <Link to="/login" className="text-[#156F5B] font-semibold hover:underline" data-testid="link-login">
              Sign in
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}
